/**
 * manpowerAssignments.js - turns the saved assignments into "who is on what,
 * which day/week", and collapses consecutive identical cells into runs so the
 * grid can draw one bar per stint.
 *
 * `edits` is the whole schedule now. There used to be a Manpower Planning
 * baseline underneath it whose sparse entries were carried forward day after
 * day, which is why the calendar arrived full of labels nobody had typed and
 * why a cell could refuse to look empty. A day shows what someone put on it,
 * and nothing otherwise.
 *
 * `details` ({ location?, projectNumber?, status? } per engineer per day, see
 * useManpower) rides along on each cell so a bar's tooltip can show them.
 */
import { ymd } from './manpowerHelpers';

export function buildAssignments(engineers, mode, days, weeks, edits, details = {}) {
  const out = new Map();
  for (const e of engineers) {
    const name = e.rawName || e.name;
    const labelFor = (key) => edits[`${name}|${key}`] || null;
    const detailFor = (key) => details?.[`${name}|${key}`] || null;
    // A week or month cell stands for many days: it shows the first details
    // found on a day carrying its winning label.
    const firstDetail = (dayKeys, code) => {
      for (const k of dayKeys) if (labelFor(k) === code && detailFor(k)) return detailFor(k);
      return null;
    };
    const entry = (code, extra, detail) => (detail ? { code, ...extra, detail } : { code, ...extra });

    const bucket = new Map();
    if (mode === 'monthly') {
      for (const d of days) {
        const key = ymd(d);
        const label = labelFor(key);
        if (label) bucket.set(key, entry(label, {}, detailFor(key)));
      }
    } else if (mode === 'yearly') {
      // 12 month buckets — per month, count days assigned to each label and
      // show the most common.
      const perMonth = new Map();
      for (const d of days) {
        const key = ymd(d);
        const label = labelFor(key);
        if (!label) continue;
        const mkey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!perMonth.has(mkey)) perMonth.set(mkey, { counts: new Map(), keys: [] });
        const c = perMonth.get(mkey);
        c.counts.set(label, (c.counts.get(label) || 0) + 1);
        c.keys.push(key);
      }
      for (const [mkey, { counts, keys }] of perMonth) {
        let best = null;
        for (const [code, n] of counts) if (!best || n > best.n) best = { code, n };
        bucket.set(mkey, entry(best.code, { days: best.n }, firstDetail(keys, best.code)));
      }
    } else {
      // Weekly + quarterly: per week, the most common label across its 7 days.
      for (const w of weeks) {
        const [y, m, d0] = w.monday.split('-').map(Number);
        const monday = new Date(y, m - 1, d0);
        const counts = new Map();
        const keys = [];
        for (let i = 0; i < 7; i++) {
          const d = new Date(monday);
          d.setDate(monday.getDate() + i);
          const key = ymd(d);
          keys.push(key);
          const label = labelFor(key);
          if (!label) continue;
          counts.set(label, (counts.get(label) || 0) + 1);
        }
        if (!counts.size) continue;
        let best = null;
        for (const [code, n] of counts) if (!best || n > best.n) best = { code, n };
        bucket.set(w.iso, entry(best.code, { days: best.n }, firstDetail(keys, best.code)));
      }
    }
    out.set(e.name, bucket);
  }
  return out;
}

/** One string per detail, for "is this the same block?" - '' when there is none. */
export const detailKey = (d) => (d ? `${d.status || ''}|${d.location || ''}|${d.projectNumber || ''}` : '');

/**
 * The details to prefill when the editor opens on an existing block: those of
 * the first day from..to that carries the block's label and has any. Null when
 * none of its days do (a block saved before details existed).
 */
export function blockDetail(edits, details, name, from, to, code) {
  if (!from || !to || !code) return null;
  const [y, m, d0] = String(from).split('-').map(Number);
  for (let d = new Date(y, m - 1, d0, 12); ymd(d) <= to; d.setDate(d.getDate() + 1)) {
    const k = `${name}|${ymd(d)}`;
    if (edits[k] === code && details?.[k]) return details[k];
  }
  return null;
}

/**
 * The details of the most recent block of `label` (the latest day carrying
 * that label that has any), for prefilling a NEW block of the same job: the
 * next week on a project is usually at the same site under the same number.
 * A PTO day is skipped: it says nothing about where the job is. Null when
 * that label has never had details.
 */
export function latestDetailFor(edits, details, label) {
  if (!label) return null;
  let bestDay = '';
  let best = null;
  for (const k of Object.keys(details || {})) {
    if (edits[k] !== label || details[k].status === 'PTO') continue;
    const day = k.slice(k.lastIndexOf('|') + 1);
    if (day > bestDay) {
      bestDay = day;
      best = details[k];
    }
  }
  return best;
}

/** What a free cell carries in the Availability view. Never a project: it lives in its own map. */
export const FREE = 'Available';

// Noon, so a DST change at local midnight cannot skip or repeat a day.
const noon = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d, 12);
};

/**
 * The Availability view: the same grid inside out. A column gets a FREE
 * entry when the engineer has nothing on some of its days, and `days` counts
 * only the free WEEKDAYS - a free Saturday is not availability. A column free
 * only at the weekend still gets an entry (days 0) so a free stretch in the
 * monthly view runs across the weekend; the grid hides a stretch with no free
 * weekday at all. Columns carry real from/to dates in every view.
 *
 * `ranges` are the free weekdays as from/to stretches inside the column (a
 * free weekend bridges, a booked day breaks), so a week or month cell can say
 * WHICH days are free, not just how many.
 */
export function buildFreeAssignments(engineers, columns, edits) {
  const out = new Map();
  for (const e of engineers) {
    const name = e.rawName || e.name;
    const bucket = new Map();
    for (const c of columns) {
      let free = false;
      let weekdays = 0;
      const ranges = [];
      let cur = null;
      for (let d = noon(c.from); ymd(d) <= c.to; d.setDate(d.getDate() + 1)) {
        const day = ymd(d);
        if (edits[`${name}|${day}`]) {
          cur = null;
          continue;
        }
        free = true;
        if (d.getDay() === 0 || d.getDay() === 6) continue;
        weekdays += 1;
        if (cur) cur.to = day;
        else {
          cur = { from: day, to: day };
          ranges.push(cur);
        }
      }
      if (free) bucket.set(c.key, { code: FREE, days: weekdays, ranges });
    }
    out.set(e.name, bucket);
  }
  return out;
}
